import React, { useState, useEffect } from 'react';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';

interface OrganizationalUnitFormProps {
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
  enterprises: any[];
}

const OrganizationalUnitForm: React.FC<OrganizationalUnitFormProps> = ({ onSubmit, onCancel, enterprises }) => {
  const { getIdToken } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    type: 'region',
    franchiseeId: '',
    parentId: '',
    enterpriseId: '',
    description: '',
  });

  const [franchisees, setFranchisees] = useState<any[]>([]);
  const [orgUnits, setOrgUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchFranchiseesAndOrgUnits = async (enterpriseId: string) => {
    if (!enterpriseId) {
      setFranchisees([]);
      setOrgUnits([]);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch franchisees
      const franchiseesResponse = await fetch(`${config.API_BASE_URL}/api/enterprises/${enterpriseId}/franchisees`, {
        headers: {
          'Authorization': `Bearer ${await getIdToken()}`,
          'Content-Type': 'application/json',
        }
      });
      if (franchiseesResponse.ok) {
        const franchiseesData = await franchiseesResponse.json();
        setFranchisees(franchiseesData.data || []);
      }

      // Fetch existing org units (for parent selection)
      const orgUnitsResponse = await fetch(`${config.API_BASE_URL}/api/enterprises/${enterpriseId}/organizational-units`, {
        headers: {
          'Authorization': `Bearer ${await getIdToken()}`,
          'Content-Type': 'application/json',
        }
      });
      if (orgUnitsResponse.ok) {
        const orgUnitsData = await orgUnitsResponse.json();
        setOrgUnits(orgUnitsData.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData.enterpriseId) {
      fetchFranchiseesAndOrgUnits(formData.enterpriseId);
    }
  }, [formData.enterpriseId]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.enterpriseId || !formData.franchiseeId) {
      alert('Unit name, enterprise, and franchisee are required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Reset dependent fields when enterprise changes
      if (field === 'enterpriseId') {
        newData.franchiseeId = '';
        newData.parentId = '';
      }
      
      return newData;
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="label-modern">Enterprise *</label>
        <select
          className="input-modern"
          value={formData.enterpriseId}
          onChange={(e) => handleChange('enterpriseId', e.target.value)}
          required
        >
          <option value="">Select Enterprise</option>
          {enterprises.map((enterprise) => (
            <option key={enterprise.enterpriseId} value={enterprise.enterpriseId}>
              {enterprise.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="label-modern">Unit Name *</label>
        <input
          type="text"
          className="input-modern"
          placeholder="Enter organizational unit name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          required
        />
      </div>

      <div className="form-group">
        <label className="label-modern">Unit Type *</label>
        <select
          className="input-modern"
          value={formData.type}
          onChange={(e) => handleChange('type', e.target.value)}
          required
        >
          <option value="region">Region</option>
          <option value="district">District</option>
          <option value="area">Area</option>
          <option value="division">Division</option>
          <option value="market">Market</option>
        </select>
      </div>

      <div className="form-group">
        <label className="label-modern">Franchisee *</label>
        <select
          className="input-modern"
          value={formData.franchiseeId}
          onChange={(e) => handleChange('franchiseeId', e.target.value)}
          required
          disabled={!formData.enterpriseId || loading}
        >
          <option value="">Select Franchisee</option>
          {franchisees.map((franchisee) => (
            <option key={franchisee.franchiseeId} value={franchisee.franchiseeId}>
              {franchisee.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="label-modern">Parent Unit (Optional)</label>
        <select
          className="input-modern"
          value={formData.parentId}
          onChange={(e) => handleChange('parentId', e.target.value)}
          disabled={!formData.enterpriseId || loading}
        >
          <option value="">No Parent Unit</option>
          {orgUnits.map((unit) => (
            <option key={unit.id} value={unit.id}>
              {unit.name} ({unit.type})
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="label-modern">Description</label>
        <textarea
          className="input-modern"
          placeholder="Enter unit description"
          rows={3}
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
        />
      </div>

      <div className="modal-footer">
        <button
          type="button"
          className="btn-modern btn-modern-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-modern btn-modern-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Create Unit'}
        </button>
      </div>
    </form>
  );
};

export default OrganizationalUnitForm;